import { useEffect, useState } from "react";
import { Pill, Trash2 } from "lucide-react";
import { ROLE_LABELS, fmt } from "@/lib/clinical";
import MedListDialog from "@/components/MedListDialog";

export default function MedicationsSection({ medications, medForm, setMedForm, saving, onSave, onDelete, isAdmin, medOptions = [], onMedListChanged }) {
  const [otherMode, setOtherMode] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  useEffect(() => { setOtherMode(false); }, [medications.length]);

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="medications-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2">
          <Pill className="h-5 w-5 text-[#00A499]" /> Medications Given
        </h2>
        {isAdmin && (
          <button onClick={() => setManageOpen(true)} data-testid="manage-med-list-btn"
            className="text-xs font-semibold text-[#005EB8] hover:underline min-h-[36px]">
            Manage list
          </button>
        )}
      </div>
      <form onSubmit={onSave} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="medication-form">
        {!otherMode ? (
          <select required value={medOptions.includes(medForm.medication) ? medForm.medication : ""}
            onChange={(e) => {
              if (e.target.value === "__other__") {
                setOtherMode(true);
                setMedForm((f) => ({ ...f, medication: "" }));
              } else {
                setMedForm((f) => ({ ...f, medication: e.target.value }));
              }
            }}
            data-testid="input-medication-name"
            className="h-12 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
            <option value="">Select medication…</option>
            {medOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            <option value="__other__">Other — type it in</option>
          </select>
        ) : (
          <div className="col-span-2 flex gap-2">
            <input required autoFocus placeholder="Type the medication name *" value={medForm.medication}
              onChange={(e) => setMedForm((f) => ({ ...f, medication: e.target.value }))}
              data-testid="input-medication-other"
              className="h-12 flex-1 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <button type="button" onClick={() => { setOtherMode(false); setMedForm((f) => ({ ...f, medication: "" })); }}
              data-testid="med-back-to-list"
              className="h-12 px-3 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
              Back to list
            </button>
          </div>
        )}
        <input placeholder="Dosage (e.g. 500mg)" value={medForm.dosage}
          onChange={(e) => setMedForm((f) => ({ ...f, dosage: e.target.value }))}
          data-testid="input-medication-dosage"
          className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        <input placeholder="Batch no." value={medForm.batch_number}
          onChange={(e) => setMedForm((f) => ({ ...f, batch_number: e.target.value }))}
          data-testid="input-medication-batch"
          className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        <input type="date" title="Expiry date" value={medForm.expiry_date}
          onChange={(e) => setMedForm((f) => ({ ...f, expiry_date: e.target.value }))}
          data-testid="input-medication-expiry"
          className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        <div className="col-span-2 sm:col-span-4 flex justify-end">
          <button type="submit" disabled={saving} data-testid="save-medication-button"
            className="h-12 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
            Record medication
          </button>
        </div>
      </form>
      {medications.length === 0 ? (
        <p className="text-sm text-slate-500 py-3 text-center" data-testid="no-medications-message">No medications recorded.</p>
      ) : (
        <div className="space-y-2">
          {medications.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 px-4 py-3" data-testid={`medication-entry-${m.id}`}>
              <div className="flex-1 min-w-[140px]">
                <p className="text-sm font-bold text-[#0F2547]">{m.medication}{m.dosage && <span className="font-semibold text-slate-600"> — {m.dosage}</span>}</p>
                <p className="text-[11px] text-slate-500 font-vitals">
                  {m.batch_number && `Batch ${m.batch_number}`}{m.batch_number && m.expiry_date && " · "}{m.expiry_date && `Exp ${m.expiry_date}`}
                </p>
              </div>
              <div className="text-[11px] text-slate-500">
                <span className="font-semibold text-[#0F2547]">{m.administered_by_name}</span>{" "}
                <span className="uppercase tracking-wide font-bold text-[#005EB8]">{ROLE_LABELS[m.administered_by_role] || m.administered_by_role}</span>
                <span className="block">{fmt(m.created_at)}</span>
              </div>
              {isAdmin && (
                <button onClick={() => onDelete(m.id)} data-testid={`delete-medication-button-${m.id}`}
                  className="text-[#D5281B] hover:bg-red-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                  title="Delete medication record (admin only)">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {isAdmin && (
        <MedListDialog open={manageOpen} onOpenChange={setManageOpen} onChanged={onMedListChanged} />
      )}
    </section>
  );
}
